/**
 * @file FiltersSection.js
 * @description Sistema completo de búsqueda y filtrado de vehículos
 * @module components/layout/FiltersSection
 * 
 * Funcionalidades principales:
 * - Sistema de filtros jerárquicos en cascada: Estado → Sitio → Subsitio → Marca → Familia
 * - Búsqueda por VIN, marca o familia
 * - Agrupación visual de resultados: Sitio → Subsitio → Tabla de vehículos
 * - Tabla con ordenamiento por columnas y paginación
 * - Indicadores de vehículos en sitio vs fuera de sitio (antenna_id)
 * - Filtros dependientes (subsitio solo disponible si hay sitio seleccionado)
 * - Auto-limpieza de filtros dependientes al cambiar filtros padres
 * - Interfaz imperativa para limpiar filtros desde componente padre
 * - **NUEVO**: Soporte para aplicar filtros externos (desde Sidebar)
 * - **NUEVO**: Columnas dinámicas y reordenables
 * 
 * IMPORTANTE: Solo muestra vehículos con lectura RFID válida y excluye estado "DETECTADO"
 */

import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Search, Car, FileText, FileDown, Settings, MapPin, AlertTriangle, HelpCircle } from 'lucide-react';
import { getVehiculosConLecturaRFID } from '../../../data/patiosData';
import { exportEstadoEnHojasPorSitio } from '../../../utils/exporters/vehiculosExporter';
import { exportEstadoPDFPorSitio } from '../../../utils/exporters/vehiculosPdfExporter';
import { useTableColumns } from '../../../hooks/useTableColumns';
import ColumnReorderPopup from '../../popups/ColumnReorderPopup';
import SitioGroup from './SitioGroup';
import { ALL_COLUMNS_CONFIG, DEFAULT_COLUMN_IDS } from '../../../data/vehicleTableConfig';
import { ESTATUS } from '../../../config/config';
import FilterDropdown from '../../../utils/filterDropdown';

// Mapeo de IDs de columnas de la tabla a claves del esquema de exportación
const COLUMN_ID_MAP = {
  vin: 'VIN',
  marca: 'Marca',
  familia: 'Familia',
  anoModelo: 'AnoModelo',
  catalogo: 'Catalogo',
  colorExt: 'ColorExterior',
  colorInt: 'ColorInterior',
  VTE_DOCTO: 'VTE_DOCTO',
  vendedor: 'vendedor',
  patio: 'BID',
  ubicacion: 'Ubicacion',
  status: 'estatus',
  estado: 'Estado',
  ultimaLectura: 'last_read',
  enSitio: 'EnSitio',
  fecha: 'FechaRecibo'
};

/**
 * =================== COMPONENTE PRINCIPAL: FiltersSection ===================
 */

/**
 * Componente principal de filtros y búsqueda de vehículos
 * 
 * Sistema de filtros en cascada:
 * - Estado → Sitio → Subsitio (jerárquico)
 * - Marca → Familia (dependiente)
 * - Búsqueda por VIN (independiente)
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.vehiculos - Array completo de vehículos
 * @param {string} props.externalMarca - Marca seleccionada externamente (desde Sidebar)
 * @param {string} props.externalStatisticType - Tipo de estadística filtrada ('inventario'|'traspaso'|'facturados')
 * @param {Function} props.onFiltersChange - Callback cuando cambian los filtros
 * @param {Function} props.onResultsChange - Callback cuando cambian los resultados
 * @param {boolean} props.loading - Indica si está cargando datos
 * @param {Object} ref - Ref para acceso imperativo desde padre
 * 
 * Métodos expuestos vía ref:
 * - clearAllFilters(): Limpia todos los filtros
 */
const FiltersSection = forwardRef(({
  vehiculos,
  externalMarca,
  externalStatisticType,
  onFiltersChange,
  onResultsChange,
  loading,
  marcasData
}, ref) => {
  // Estado de filtros
  const [filters, setFilters] = useState({
    estado: '',
    sitio: '',
    subsitio: '',
    marca: '',
    familia: '',
    searchTerm: '',
    statistic: '' // 'inventario', 'traspaso', 'facturados'
  });

  const [isColumnPopupOpen, setIsColumnPopupOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(null);


  // ===== HOOK DE COLUMNAS DINÁMICAS =====
  const {
    visibleColumns,
    saveColumnOrder,
  } = useTableColumns('filters_section_table', ALL_COLUMNS_CONFIG, DEFAULT_COLUMN_IDS);

  /**
   * EFECTO CRÍTICO: Aplica filtros externos cuando cambian
   * Se ejecuta cuando el Sidebar envía una marca o estadística
   */
  useEffect(() => {
    if (externalMarca) {
      setFilters(prev => ({
        ...prev,
        marca: externalMarca,
        statistic: externalStatisticType || ''
      }));
    }
  }, [externalMarca, externalStatisticType]);

  /**
   * Filtra vehículos base: solo con RFID y excluyendo estado "DETECTADO"
   * Este es el conjunto inicial antes de aplicar filtros de usuario
   */
  const vehiculosConRFID = useMemo(() => {
    const conRFID = getVehiculosConLecturaRFID(vehiculos || []);
    return conRFID.filter(v => {
      const estado = v.lectura_rfid?.patio_estado;
      return estado && estado.toUpperCase() !== 'DETECTADO';
    });
  }, [vehiculos]);

  /**
   * Calcula opciones disponibles para cada filtro
   * Implementa lógica de filtros en cascada:
   * - Estados: siempre todos disponibles
   * - Sitios: filtrados por estado (si está seleccionado)
   * - Subsitios: REQUIERE sitio seleccionado, filtrados por estado + sitio
   * - Marcas: filtradas por estado + sitio + subsitio
   * - Familias: filtradas por todos los anteriores + marca
   */
  const filterOptions = useMemo(() => {
    if (!vehiculosConRFID || vehiculosConRFID.length === 0) {
      return { estado: [], sitio: [], subsitio: [], marca: [], familia: [] };
    }

    // Estados - siempre todos disponibles
    const estados = [...new Set(vehiculosConRFID.map(v => v.lectura_rfid?.patio_estado).filter(Boolean))].sort();

    // Sitios - filtrados por estado si está seleccionado
    let sitioData = vehiculosConRFID;
    if (filters.estado) {
      sitioData = sitioData.filter(v => v.lectura_rfid?.patio_estado === filters.estado);
    }
    const sitios = [...new Set(sitioData.map(v => v.lectura_rfid?.patio_nombre).filter(Boolean))].sort();

    // Subsitios - REQUIERE sitio seleccionado
    let subsitioData = [];
    if (filters.sitio) {
      subsitioData = vehiculosConRFID;
      if (filters.estado) {
        subsitioData = subsitioData.filter(v => v.lectura_rfid?.patio_estado === filters.estado);
      }
      subsitioData = subsitioData.filter(v => v.lectura_rfid?.patio_nombre === filters.sitio);
    }
    const subsitios = [...new Set(subsitioData.map(v => v.lectura_rfid?.subpatio_nombre).filter(Boolean))].sort();

    // Marcas - filtradas por estado, sitio y subsitio
    let marcaData = vehiculosConRFID;
    if (filters.estado) {
      marcaData = marcaData.filter(v => v.lectura_rfid?.patio_estado === filters.estado);
    }
    if (filters.sitio) {
      marcaData = marcaData.filter(v => v.lectura_rfid?.patio_nombre === filters.sitio);
    }
    if (filters.subsitio) {
      marcaData = marcaData.filter(v => v.lectura_rfid?.subpatio_nombre === filters.subsitio);
    }
    const marcas = [...new Set(marcaData.map(v => v.Marca).filter(Boolean))].sort();

    // Familias - filtradas por todo lo anterior
    let familiaData = marcaData;
    if (filters.marca) {
      familiaData = familiaData.filter(v => v.Marca === filters.marca);
    }
    const familias = [...new Set(familiaData.map(v => v.Familia || v.Modelo).filter(Boolean))].sort();

    return { estado: estados, sitio: sitios, subsitio: subsitios, marca: marcas, familia: familias };
  }, [vehiculosConRFID, filters.estado, filters.sitio, filters.subsitio, filters.marca]);

  /**
   * Filtra y agrupa resultados según filtros activos
   * 
   * Proceso:
   * 1. Aplica todos los filtros seleccionados
   * 2. Filtra por tipo de estadística (inventario/traspaso/facturados)
   * 3. Agrupa por: Sitio → Subsitio → Array de vehículos
   * 4. Retorna estructura jerárquica para visualización
   * 
   * @returns {Object} Objeto con estructura: { sitioNombre: { estado, totalVehiculos, subsitios: {} } }
   */
  const groupedResults = useMemo(() => {
    if (!vehiculosConRFID || vehiculosConRFID.length === 0) {
      return {};
    }

    let filtered = vehiculosConRFID;

    // Aplicar filtros
    if (filters.estado) {
      filtered = filtered.filter(v => v.lectura_rfid?.patio_estado === filters.estado);
    }
    if (filters.sitio) {
      filtered = filtered.filter(v => v.lectura_rfid?.patio_nombre === filters.sitio);
    }
    if (filters.subsitio) {
      filtered = filtered.filter(v => v.lectura_rfid?.subpatio_nombre === filters.subsitio);
    }
    if (filters.marca) {
      filtered = filtered.filter(v => v.Marca === filters.marca);
    }
    if (filters.familia) {
      filtered = filtered.filter(v => (v.Familia || v.Modelo) === filters.familia);
    }
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(v =>
        v.VIN?.toLowerCase().includes(searchLower) ||
        v.Marca?.toLowerCase().includes(searchLower) ||
        (v.Familia || v.Modelo)?.toLowerCase().includes(searchLower)
      );
    }

    // FILTRO POR TIPO DE ESTADÍSTICA
    if (filters.statistic) {
      switch (filters.statistic) {
        case 'inventario':
          // estatus = 1 (Inventario)
          filtered = filtered.filter(v => v.estatus === ESTATUS.INVENTARIO);
          break;
        case 'traspaso':
          // estatus = 3 (Traspaso sin recibir / Compradas por llegar)
          filtered = filtered.filter(v => v.estatus === ESTATUS.EN_TRANSITO);
          break;
        case 'facturados':
          // estatus = 6 (Facturados no entregados)
          filtered = filtered.filter(v => v.estatus === ESTATUS.FACTURADO_NO_ENTREGADO);
          break;
        case 'entregados':
          // estatus = 7 (Control de facturados no entregados)
          filtered = filtered.filter(v => v.estatus === ESTATUS.ENTREGADO_STANDBY);
          break;
        default:
          break;
      }
    }

    // Agrupar por Sitio → Subsitio
    const grouped = {};

    filtered.forEach(vehiculo => {
      const estado = vehiculo.lectura_rfid?.patio_estado || 'Sin estado';
      const sitio = vehiculo.lectura_rfid?.patio_nombre || 'Sin sitio';
      const subsitio = vehiculo.lectura_rfid?.subpatio_nombre || 'Sin subsitio';

      if (!grouped[sitio]) {
        grouped[sitio] = {
          estado: estado,
          totalVehiculos: 0,
          subsitios: {}
        };
      }
      if (!grouped[sitio].subsitios[subsitio]) {
        grouped[sitio].subsitios[subsitio] = [];
      }

      grouped[sitio].subsitios[subsitio].push(vehiculo);
      grouped[sitio].totalVehiculos++;
    });

    return grouped;
  }, [vehiculosConRFID, filters]);

  /**
 * Estructura de exportación: sitios → secciones (subsitios con vehículos)
 * Reutiliza la misma forma que usan los servicios:
 *   [{ sitio: string, secciones: [{ nombre, vehiculos: [...] }] }, ...]
 */
  const exportSitesStructure = useMemo(() => {
    const sitios = [];

    Object.entries(groupedResults).forEach(([sitioNombre, sitioData]) => {
      const secciones = Object.entries(sitioData.subsitios || {})
        .map(([subNombre, vehiculos]) => ({
          nombre: subNombre,
          vehiculos: vehiculos || [],
        }))
        .filter(sec => sec.vehiculos.length > 0);

      if (secciones.length > 0) {
        sitios.push({
          sitio: sitioNombre,
          secciones,
          estado: sitioData.estado || '',
        });
      }
    });

    return sitios;
  }, [groupedResults]);



  /**
   * Calcula total de vehículos en resultados filtrados
   */
  const totalResultados = useMemo(() => {
    return Object.values(groupedResults).reduce((sum, sitio) => sum + sitio.totalVehiculos, 0);
  }, [groupedResults]);

  /**
   * Limpia todos los filtros
   */
  const clearFilters = useCallback(() => {
    setFilters({
      estado: '',
      sitio: '',
      subsitio: '',
      marca: '',
      familia: '',
      searchTerm: '',
      statistic: ''
    });
  }, []);

  /**
   * Maneja cambios en filtros individuales
   * Implementa lógica de limpieza en cascada:
   * - Si cambia estado → limpia sitio, subsitio, marca, familia
   * - Si cambia sitio → limpia subsitio, marca, familia
   * - Si cambia subsitio → limpia marca, familia
   * - Si cambia marca → limpia familia y estadística
   */
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [filterName]: value };

      // Auto-limpieza de filtros dependientes en cascada
      if (filterName === 'estado') {
        // Si cambia estado, limpiar todo lo que depende de ubicación
        newFilters.sitio = '';
        newFilters.subsitio = '';
        newFilters.marca = '';
        newFilters.familia = '';
        newFilters.statistic = '';
      }

      if (filterName === 'sitio') {
        // Si cambia sitio, limpiar subsitio y filtros de marca/familia
        newFilters.subsitio = '';
        newFilters.marca = '';
        newFilters.familia = '';
        newFilters.statistic = '';
      }

      if (filterName === 'subsitio') {
        // Si cambia subsitio, limpiar marca y familia
        newFilters.marca = '';
        newFilters.familia = '';
        newFilters.statistic = '';
      }

      if (filterName === 'marca') {
        // Si cambia marca, limpiar familia y estadística
        newFilters.familia = '';
        newFilters.statistic = '';
      }

      return newFilters;
    });
  }, []);

  /**
   * Expone métodos para acceso imperativo desde componente padre
   */
  useImperativeHandle(ref, () => ({
    clearAllFilters: clearFilters,
    applyMarcaFilter: (marca) => {
      console.log('🎯 Aplicando filtro de marca:', marca);
      setFilters(prev => ({
        ...prev,
        marca: marca,
        statistic: ''
      }));
    },
    applyStatisticFilter: (marca, statisticType) => {
      console.log('🎯 Aplicando filtro de estadística:', marca, statisticType);
      setFilters(prev => ({
        ...prev,
        marca: marca,
        statistic: statisticType
      }));
    },
    scrollTo: () => {
      if (containerRef.current) {
        // Scroll hacia el contenedor de filtros
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }), [clearFilters]);

  // Calcular si hay filtros activos
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    // No contar 'statistic' como filtro visible
    if (key === 'statistic') return false;
    return value !== '';
  });

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'statistic') return false;
    return value !== '';
  }).length;

  // Mensaje descriptivo del filtro de estadística activo
  const statisticLabel = useMemo(() => {
    switch (filters.statistic) {
      case 'inventario':
        return 'Inventario';
      case 'traspaso':
        return 'Unidades por arribar';
      case 'facturados':
        return 'Unidades facturadas';
      default:
        return '';
    }
  }, [filters.statistic]);

  const handleExportExcel = useCallback(() => {
    if (!exportSitesStructure.length) {
      console.warn('No hay resultados filtrados para exportar a Excel');
      return;
    }

    // Usar las columnas visibles y su orden actual, mapeadas al esquema
    const visibleColumnIds = visibleColumns
      .map(col => COLUMN_ID_MAP[col.id])
      .filter(Boolean); // Filtrar si alguna columna no tiene mapeo

    const meta = {
      // si hay un estado seleccionado lo usamos, si no, decimos “Varios estados”
      estado: filters.estado || 'Varios estados (filtros avanzados)',
    };

    // Reutilizamos el servicio que ya existe
    exportEstadoEnHojasPorSitio(exportSitesStructure, visibleColumnIds, meta);
    console.log('✅ Excel (filtros avanzados) exportado');
  }, [exportSitesStructure, filters.estado, visibleColumns]);

  const handleExportPDF = useCallback(() => {
    if (!exportSitesStructure.length) {
      console.warn('No hay resultados filtrados para exportar a PDF');
      return;
    }

    // Usar las columnas visibles y su orden actual, mapeadas al esquema
    const visibleColumnIds = visibleColumns
      .map(col => COLUMN_ID_MAP[col.id])
      .filter(Boolean);

    const meta = {
      estado: filters.estado || 'Varios estados (filtros avanzados)',
    };

    // Reutilizamos el servicio de PDF existente
    exportEstadoPDFPorSitio(exportSitesStructure, visibleColumnIds, meta);
    console.log('✅ PDF (filtros avanzados) exportado');
  }, [exportSitesStructure, filters.estado, visibleColumns]);

  // Ref para scroll automático
  const containerRef = React.useRef(null);

  // Calcular el valor "esperado" desde el sidebar para la marca/estadística activa
  const sidebarExpectedCount = useMemo(() => {
    if (!marcasData?.length || !filters.marca) return null;

    const marcaData = marcasData.find(m => m.marca === filters.marca);
    if (!marcaData) return null;

    switch (filters.statistic) {
      case 'inventario':
        return marcaData.inventario || 0;
      case 'traspaso':
        return marcaData.enTransito || 0;
      case 'facturados':
        return marcaData.facturadosNoEntregados || 0;
      default:
        // Solo marca sin estadística: granTotal
        return marcaData.granTotal || 0;
    }
  }, [marcasData, filters.marca, filters.statistic]);

  // Calcular Sin RFID = esperado - encontrados
  const sinRFIDCount = useMemo(() => {
    if (sidebarExpectedCount === null) return 0;
    return Math.max(0, sidebarExpectedCount - totalResultados);
  }, [sidebarExpectedCount, totalResultados]);

  // Calcular desglose por patio desde groupedResults
  const patiosBreakdown = useMemo(() => {
    return Object.entries(groupedResults)
      .map(([nombre, data]) => ({ nombre, count: data.totalVehiculos }))
      .sort((a, b) => b.count - a.count);
  }, [groupedResults]);

  return (
    <div ref={containerRef} className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
      {/* Header con título y botón de limpiar */}
      <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
        <div className="min-w-0">
          <h2 className="text-base sm:text-xl font-bold text-gray-900">Búsqueda de vehículos</h2>
          <div className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 flex items-center flex-wrap gap-1">
            <span>Vehículos en sitios • {totalResultados} vehículo(s) disponible(s)</span>

            {/* Tooltip en icono de ayuda */}
            {sidebarExpectedCount !== null && (
              <div className="relative group flex items-center">
                <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />

                <div className="absolute top-full left-0 mt-2 bg-gray-900 text-white p-3 rounded-lg text-xs 
                                opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none 
                                z-50 w-64 shadow-xl">
                  {/* Título */}
                  <div className="font-semibold mb-2 text-gray-100">
                    {filters.statistic ? statisticLabel : `Marca ${filters.marca}`} — Desglose
                  </div>

                  {/* Patios */}
                  <div className="space-y-1 mb-2">
                    {patiosBreakdown.map(({ nombre, count }) => (
                      <div key={nombre} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1 min-w-0">
                          <MapPin className="w-3 h-3 text-blue-400 shrink-0" />
                          <span className="text-gray-300 truncate">{nombre}:</span>
                        </div>
                        <span className="font-medium shrink-0">{count}</span>
                      </div>
                    ))}
                  </div>

                  {/* Sin RFID */}
                  {sinRFIDCount > 0 && (
                    <div className="flex items-center justify-between gap-2 text-yellow-300">
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 shrink-0" />
                        <span>Sin RFID:</span>
                      </div>
                      <span className="font-medium">+{sinRFIDCount}</span>
                    </div>
                  )}

                  {/* Línea de total */}
                  <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between font-semibold">
                    <span>= Total esperado:</span>
                    <span className="text-white">{sidebarExpectedCount}</span>
                  </div>
                </div>
              </div>
            )}

            {filters.statistic && (
              <span className="ml-1 sm:ml-2 text-blue-600 font-medium">
                • Filtrando: {statisticLabel}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2 shrink-0">
          {(hasActiveFilters || filters.statistic) && (
            <button
              onClick={clearFilters}
              className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors whitespace-nowrap"
            >
              Limpiar filtros
            </button>
          )}

          {(hasActiveFilters || filters.statistic) && totalResultados > 0 && (
            <>
              <button
                onClick={handleExportExcel}
                className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200"
                title="Exportar a Excel"
              >
                <FileDown className="w-4 h-4" />
              </button>

              <button
                onClick={handleExportPDF}
                className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200"
                title="Exportar a PDF"
              >
                <FileText className="w-4 h-4" />
              </button>

              <button
                onClick={() => setIsColumnPopupOpen(true)}
                className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg transition-colors border border-gray-300"
                title="Configurar columnas"
              >
                <Settings className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>


      {/* Grid de filtros */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
        {/* Estado */}
        <div>
          <FilterDropdown
            label="Estado"
            value={filters.estado}
            options={filterOptions.estado}
            placeholder="Todos los estados"
            onChange={(val) => handleFilterChange('estado', val)}
            withSearch={true}
            isOpen={openFilter === 'estado'}
            onOpenChange={(open) => setOpenFilter(open ? 'estado' : null)}
          />
        </div>
        {/* Sitio */}
        <div>
          <div>
            <FilterDropdown
              label="Sitio"
              value={filters.sitio}
              options={filterOptions.sitio}
              placeholder="Todos los sitios"
              onChange={(val) => handleFilterChange('sitio', val)}
              withSearch={true}
              isOpen={openFilter === 'sitio'}
              onOpenChange={(open) => setOpenFilter(open ? 'sitio' : null)}
            />
          </div>
        </div>

        {/* Subsitio (deshabilitado si no hay sitio) */}
        <div>
          <FilterDropdown
            label="Subsitio"
            value={filters.subsitio}
            options={filterOptions.subsitio}
            placeholder="Todos los subsitios"
            onChange={(val) => handleFilterChange('subsitio', val)}
            disabled={!filters.sitio}
            withSearch={true}
            isOpen={openFilter === 'subsitio'}
            onOpenChange={(open) => setOpenFilter(open ? 'subsitio' : null)}
          />
        </div>

        {/* Marca */}
        <div>
          <FilterDropdown
            label="Marca"
            value={filters.marca}
            options={filterOptions.marca}
            placeholder="Todas las marcas"
            onChange={(val) => handleFilterChange('marca', val)}
            withSearch={true}
            isOpen={openFilter === 'marca'}
            onOpenChange={(open) => setOpenFilter(open ? 'marca' : null)}
          />
        </div>

        {/* Familia */}
        <div>
          <FilterDropdown
            label="Familia"
            value={filters.familia}
            options={filterOptions.familia}
            placeholder="Todas las familias"
            onChange={(val) => handleFilterChange('familia', val)}
            withSearch={true}
            isOpen={openFilter === 'familia'}
            onOpenChange={(open) => setOpenFilter(open ? 'familia' : null)}
          />
        </div>

        {/* Búsqueda por VIN */}
        <div className="flex flex-col gap-1">
          <label className="block text-sm font-medium text-gray-700">Buscar VIN</label>
          <div className="relative">
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              placeholder="Buscar..."
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Contador de filtros y resultados */}
      {(hasActiveFilters || filters.statistic) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-y-1 text-xs sm:text-sm">
          <div className="text-gray-600 flex items-center flex-wrap gap-1">
            {activeFilterCount + (filters.statistic ? 1 : 0)} filtro(s) activo(s)
            {filters.statistic && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {statisticLabel}
              </span>
            )}
          </div>


        </div>
      )}

      {/* Resultados agrupados */}
      {(hasActiveFilters || filters.statistic) && Object.keys(groupedResults).length > 0 && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Resultados
          </h3>
          <div className="space-y-4">
            {Object.entries(groupedResults).map(([sitioNombre, sitioData]) => (
              <SitioGroup
                key={sitioNombre}
                sitioNombre={sitioNombre}
                sitioData={sitioData}
                estado={sitioData.estado}
                visibleColumns={visibleColumns}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sin resultados */}
      {(hasActiveFilters || filters.statistic) && Object.keys(groupedResults).length === 0 && (
        <div className="border-t border-gray-200 pt-6">
          <div className="text-center py-12">
            <Car className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-900 mb-2">No se encontraron vehículos</p>
            <p className="text-sm text-gray-500">Intenta ajustar los filtros de búsqueda</p>
          </div>
        </div>
      )}

      {/* Mensaje inicial */}
      {!hasActiveFilters && !filters.statistic && (
        <div className="border-t border-gray-200 pt-6">
          <div className="text-center py-12">
            <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-900 mb-2">Selecciona filtros para buscar</p>
            <p className="text-sm text-gray-500">Usa los filtros de arriba para encontrar vehículos</p>
          </div>
        </div>
      )}

      <ColumnReorderPopup
        isOpen={isColumnPopupOpen}
        onClose={() => setIsColumnPopupOpen(false)}
        allColumns={ALL_COLUMNS_CONFIG}
        currentColumns={visibleColumns}
        onSave={saveColumnOrder}
        defaultColumnsIds={DEFAULT_COLUMN_IDS}
      />
    </div>
  );
});

FiltersSection.displayName = 'FiltersSection';
export default FiltersSection;